// ── New Task modal draft persistence (C1512) ──
//
// Remembers an in-progress New Task modal per project so an accidental dismissal
// (Escape, backdrop click, route navigation — anything that isn't the user explicitly
// saying "throw this away") doesn't lose everything typed. Modelled on the Task App's
// saveDraft/loadDraft/clearDraft trio (ai/todo/server/src/client/utils.js) — same
// JSON-in-storage shape, same try/catch-swallowed-on-both-sides discipline as this
// repo's own best-practice storage callers (ui.js's tt-project-theme cache,
// layout.js's 2FA-banner dismissal, lib/task-app-link.js's URL override).
//
// Key: 'tt-new-task-draft:<projectId>' — 'tt-' prefix + ':' separator, matching
// ui.js's 'tt-project-theme:<id>' family (see tt-web-board.md for the full contract).
// projectId is a REQUIRED arg on all three accessors below — a missing/blank one is a
// no-op (never falls back to a shared/global key), so a draft can never leak across
// projects through an unscoped bucket.

const KEY_PREFIX = 'tt-new-task-draft:';

// Pre-scoping key shape: one global draft shared by every project. loadNewTaskDraft()
// deletes it on every call and never adopts its contents — handing it to whichever
// project happens to open the modal first would be the exact cross-project leak this
// module exists to prevent. Can never collide with a scoped 'tt-new-task-draft:<id>' key.
const LEGACY_KEY = 'tt-new-task-draft';

// storageKey(projectId) — null for a missing/blank id, so callers treat it as "no storage"
// instead of falling through to 'tt-new-task-draft:null'/':undefined' — a single bucket
// that every project without a resolved id would otherwise share.
function storageKey(projectId) {
  if (projectId === null || projectId === undefined || projectId === '') return null;
  return KEY_PREFIX + projectId;
}

const VALID_CATEGORIES = new Set(['CODING', 'HUMAN']);

// normalizeDraft(raw) — coerces arbitrary parsed JSON (or an already-shaped draft
// object) into the known draft shape, dropping anything malformed rather than
// throwing. Returns null when nothing usable survives, so callers can treat "no
// draft" and "garbage draft" identically.
export function normalizeDraft(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const title = typeof raw.title === 'string' ? raw.title : '';
  const description = typeof raw.description === 'string' ? raw.description : '';
  const tags = Array.isArray(raw.tags) ? raw.tags.filter(t => typeof t === 'string' && t) : [];
  const category = VALID_CATEGORIES.has(raw.category) ? raw.category : 'CODING';
  const assignee = Number.isFinite(raw.assignee) ? raw.assignee : null;
  const priority = Number.isFinite(raw.priority) ? raw.priority : null;
  const uploads = Array.isArray(raw.uploads)
    ? raw.uploads.filter(u => u && typeof u.url === 'string' && (u.kind === 'images' || u.kind === 'files'))
      .map(u => ({ kind: u.kind, url: u.url }))
    : [];

  const draft = { title, description, tags, category, assignee, priority, uploads };
  return isEmptyDraft(draft) ? null : draft;
}

// isEmptyDraft(draft) — true when there's nothing worth remembering: no text, no tags,
// no uploads. category/assignee/priority alone (form defaults) don't count — a modal
// opened and closed untouched must not leave a draft behind.
export function isEmptyDraft(draft) {
  if (!draft) return true;
  return !draft.title?.trim() && !draft.description?.trim()
    && !(draft.tags && draft.tags.length) && !(draft.uploads && draft.uploads.length);
}

export function loadNewTaskDraft(projectId) {
  try {
    localStorage.removeItem(LEGACY_KEY); // one-time purge of the old unscoped key, every load
    const key = storageKey(projectId);
    if (!key) return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return normalizeDraft(JSON.parse(raw));
  } catch {
    return null; // corrupt JSON, or storage inaccessible — treat as no draft
  }
}

export function saveNewTaskDraft(projectId, draft) {
  try {
    const key = storageKey(projectId);
    if (!key) return; // no projectId — never fall back to a shared bucket
    if (isEmptyDraft(draft)) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(draft));
    }
  } catch {
    /* best-effort draft cache only — a full/blocked storage must not break the modal */
  }
}

export function clearNewTaskDraft(projectId) {
  try {
    const key = storageKey(projectId);
    if (!key) return;
    localStorage.removeItem(key);
  } catch {
    /* nothing to clean up if storage isn't reachable in the first place */
  }
}

// attachmentDeletePath(url) — '/api/projects/2/images/57' (or the same path prefixed
// with an absolute origin) -> '/projects/2/images/57', ready for api.delete() (which
// prefixes '/api' itself). Deliberately narrow — same ref shape as
// lib/attachment-preview.js's REF_RE — so this can never be pointed at an arbitrary
// URL. Returns null for anything that doesn't match, including the empty-parens
// upload-in-progress placeholder.
const ATTACHMENT_URL_RE = /^(?:https?:\/\/[^/]+)?\/api(\/projects\/\d+\/(?:images|files)\/\d+)$/;

export function attachmentDeletePath(url) {
  const m = ATTACHMENT_URL_RE.exec(String(url || ''));
  return m ? m[1] : null;
}
